import React from 'react';
import BreadcrumbGroup from '@amzn/awsui-components-core/breadcrumb-group';
import { SpaceBetween } from '@amzn/awsui-components-core';
import {
  LuDatabase,
  LuFileText,
  LuDownload,
  LuZap,
  LuSearch,
  LuBarChart2,
  LuShield,
  LuRefreshCw,
  LuLayers,
  LuGitBranch,
  LuTerminal,
  LuCode,
  LuGlobe,
  LuMessageSquare,
  LuSettings,
  LuCpu,
  LuTable2,
  LuUpload,
  LuExternalLink,
  LuVideo
} from 'react-icons/lu';
import { IconType } from 'react-icons';
import { McpConfig } from 'src/components/context/AppContextModels';
import { SLACK_ICON_URL } from 'src/constants/AppConstants';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import InstallTerminal from './InstallTerminal';
import styles from './McpServerDetailPage.module.scss';

interface McpServerDetailPageProps {
  server: McpConfig;
  onBack: () => void;
}

const LUCIDE_ICON_MAP: Record<string, IconType> = {
  database: LuDatabase,
  'file-text': LuFileText,
  download: LuDownload,
  zap: LuZap,
  search: LuSearch,
  'bar-chart-2': LuBarChart2,
  shield: LuShield,
  'refresh-cw': LuRefreshCw,
  layers: LuLayers,
  'git-branch': LuGitBranch,
  terminal: LuTerminal,
  code: LuCode,
  globe: LuGlobe,
  'message-square': LuMessageSquare,
  settings: LuSettings,
  cpu: LuCpu,
  table: LuTable2,
  upload: LuUpload
};

const RESOURCE_ICON_MAP: Record<string, React.ReactNode> = {
  chat: <img src={SLACK_ICON_URL} alt="" width={18} height={18} />,
  document: <LuFileText size={18} />,
  globe: <LuGlobe size={18} />,
  video: <LuVideo size={18} />,
  code: <LuCode size={18} />,
  settings: <LuSettings size={18} />
};

const McpServerDetailPage: React.FC<McpServerDetailPageProps> = ({ server, onBack }) => {
  const toolGroups = server.toolGroups || [];
  const totalTools = toolGroups.reduce((sum, g) => sum + g.tools.length, 0);

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <BreadcrumbGroup
        items={[
          { text: 'MCP Servers', href: '#' },
          { text: server.name, href: '#' }
        ]}
        onFollow={(e) => {
          e.preventDefault();
          if (e.detail.text === 'MCP Servers') onBack();
        }}
      />

      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>{server.name}</h1>
        <p className={styles.subtitle}>{server.description}</p>
      </div>

      {/* What It Does — Feature Cards */}
      {server.features && server.features.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>What It Does</h4>
          <div className={styles.featureCardsRow}>
            {server.features.map((feature, idx) => {
              const IconComp = LUCIDE_ICON_MAP[feature.icon] || LuZap;
              return (
                <div key={idx} className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <IconComp size={20} />
                  </div>
                  <div className={styles.featureText}>
                    <span className={styles.featureTitle}>{feature.title}</span>
                    <span className={styles.featureDescription}>{feature.description}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Install */}
      {server.terminalCommands && server.terminalCommands.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Install</h4>
          <p className={styles.sectionSubtitle}>Select your operating system and install method, then copy the command below into your terminal.</p>
          <InstallTerminal commands={server.terminalCommands} />
        </div>
      )}

      {/* Getting Help — Resources */}
      {server.resources && server.resources.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Getting Help</h4>
          <div className={styles.resourceCardsRow}>
            {(server.resources || []).map((resource, idx) => (
              <a key={idx} href={resource.url} target="_blank" rel="noopener noreferrer" className={styles.resourceCard}>
                <div className={styles.resourceIcon}>{RESOURCE_ICON_MAP[resource.type || ''] || <LuGlobe size={18} />}</div>
                <div className={styles.resourceText}>
                  <span className={styles.resourceLabel}>{resource.label}</span>
                </div>
                <LuExternalLink size={14} className={styles.resourceExternal} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Contacts */}
      {server.contacts && server.contacts.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>Contacts</h4>
          <div className={styles.contactsRow}>
            {server.contacts.map((contact, idx) => (
              <a
                key={idx}
                href={`https://phonetool.amazon.com/users/${contact.alias}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.contactPill}
              >
                <Avatar username={contact.alias} size={AvatarSize.ExtraSmall} />
                <span className={styles.contactName}>{contact.alias}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* MCP Skills — 2 per row */}
      {toolGroups.length > 0 && (
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>
            MCP Skills <span className={styles.badge}>{totalTools}</span>
          </h4>
          {toolGroups.map((group) => (
            <div key={group.category} className={styles.toolCategory}>
              <div className={styles.toolCategoryHeader}>{group.category.toUpperCase()}</div>
              <div className={styles.toolsGrid}>
                {group.tools.map((tool, idx) => (
                  <div key={idx} className={styles.toolCard}>
                    <div className={styles.toolIcon}>
                      <LuTerminal size={16} />
                    </div>
                    <div className={styles.toolContent}>
                      <code className={styles.toolName}>{tool.name}</code>
                      {tool.description && <span className={styles.toolDescription}>{tool.description}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default McpServerDetailPage;
